import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarApi, customerApi } from '../api';
import { useAuth } from '../AuthContext';
import { exportCsv } from '../components/exportCsv';
import { IconDownload, IconPlusCircle, IconCrown, IconCake } from '../components/icons';
import { Empty, ErrorNote, Modal, Spinner, dateOf, money, th, todayIso, useLoader } from '../components/common';

const GENDER_TH = { female: 'หญิง', male: 'ชาย', other: 'อื่น ๆ', unspecified: 'ไม่ระบุ' };

/** ลูกค้า — the member list. Org-scoped, so it ignores the branch switcher. */
export default function Customers() {
  const navigate = useNavigate();
  const { branches, branchId } = useAuth();
  const [q, setQ] = useState('');
  const [branchScope, setBranchScope] = useState('all');
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ sort: 'total_spend', dir: 'desc' });
  const [creating, setCreating] = useState(false);

  const list = useLoader(
    () => customerApi.list({
      q,
      ...filters,
      ...sort,
      branchId: branchScope === 'all' ? undefined : branchScope,
    }),
    [q, branchScope, JSON.stringify(filters), JSON.stringify(sort)],
  );
  const lookups = useLoader(() => calendarApi.lookups(), []);

  const sortBy = (key) =>
    setSort((s) => ({ sort: key, dir: s.sort === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const arrow = (key) => (sort.sort === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <>
      <div className="toolbar">
        {/* ลูกค้า heads at 1.6rem where แคชเชียร์ heads at 20px — measured on
            each page, so the larger size is this screen's own. */}
        <h2 className="lg" style={{ margin: 0 }}>ลูกค้า ({list.data?.total ?? 0} คน)</h2>
        <div style={{ flex: 1 }} />
        <button className="btn-outline" onClick={() => setCreating(true)}><IconPlusCircle size={16} /> สร้างลูกค้า</button>
        <button
          className="btn-solid"
          disabled={!list.data?.customers?.length}
          onClick={() => exportCsv('customers', [
            { label: 'รหัสสมาชิก', value: (c) => c.memberCode },
            { label: 'ชื่อเล่น', value: (c) => c.nickname || '' },
            { label: 'ชื่อจริง', value: (c) => c.firstName || '' },
            { label: 'นามสกุล', value: (c) => c.lastName || '' },
            { label: 'เบอร์โทรศัพท์', value: (c) => c.phone || '' },
            { label: 'ความต้องการพิเศษล่าสุด', value: (c) => c.latestRequirement || '' },
            { label: 'ยอดรวมการซื้อ', value: (c) => c.totalSpend },
            { label: 'จำนวนครั้งที่มา', value: (c) => c.visitCount },
            { label: 'มาครั้งล่าสุดเมื่อ',
              value: (c) => dateOf(c.lastVisitAt) },
          ], list.data.customers)}
        >
          <IconDownload size={16} /> ดาวน์โหลดข้อมูล
        </button>
      </div>

      <div className="toolbar">
        <select
          className="chip"
          value={branchScope}
          onChange={(e) => setBranchScope(e.target.value)}
          aria-label="เลือกสาขาลูกค้า"
        >
          <option value="all">ทุกสาขา</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>สาขา {b.code}</option>
          ))}
        </select>
        <input
          className="header-search"
          placeholder="ค้นหาลูกค้า"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button
          className={`chip${filters.tierId ? ' active' : ''}`}
          title="เฉพาะสมาชิกที่มีระดับ"
          onClick={() => setFilters((f) => ({
            ...f,
            tierId: f.tierId ? undefined : lookups.data?.customerTiers?.[0]?.id,
          }))}
        >
          <IconCrown size={17} />
        </button>
        <button
          className={`chip${filters.birthdayMonth ? ' active' : ''}`}
          title="วันเกิดเดือนนี้"
          onClick={() => setFilters((f) => ({
            ...f,
            birthdayMonth: f.birthdayMonth ? undefined : Number(todayIso().slice(5, 7)),
          }))}
        >
          <IconCake size={17} />
        </button>

        <div style={{ flex: 1 }} />

        <select className="chip" value={filters.gender || ''}
                onChange={(e) => setFilters((f) => ({ ...f, gender: e.target.value || undefined }))}>
          <option value="">เพศ · All</option>
          {['female', 'male', 'other', 'unspecified'].map((g) => (
            <option key={g} value={g}>{g === 'female' ? 'หญิง' : g === 'male' ? 'ชาย' : th(g)}</option>
          ))}
        </select>

        <select className="chip" value={filters.ageBandId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, ageBandId: e.target.value || undefined }))}>
          <option value="">อายุ · All</option>
          {(lookups.data?.ageBands || []).map((b) => (
            <option key={b.id} value={b.id}>{b.label_th || b.label_en}</option>
          ))}
        </select>

        <select className="chip" value={filters.nationalityCode || ''}
                onChange={(e) => setFilters((f) => ({ ...f, nationalityCode: e.target.value || undefined }))}>
          <option value="">สัญชาติ · All</option>
          {(lookups.data?.nationalities || []).map((n) => (
            <option key={n.code} value={n.code}>{n.name_th || n.name_en}</option>
          ))}
        </select>

        <select className="chip" value={filters.occupationId || ''}
                onChange={(e) => setFilters((f) => ({ ...f, occupationId: e.target.value || undefined }))}>
          <option value="">อาชีพ · All</option>
          {(lookups.data?.occupations || []).map((o) => (
            <option key={o.id} value={o.id}>{o.name_th || o.name_en}</option>
          ))}
        </select>
      </div>

      <div className="card customer-table-card">
        {list.loading ? <Spinner />
          : list.error ? <ErrorNote error={list.error} />
          : !list.data.customers.length ? <Empty icon="customer" />
          : (
            <div className="customer-table-wrap">
              <table className="data customer-table">
                <colgroup>
                  {[126, 110, 140, 130, 220, 130, 130, 150, 110, 110, 170, 110, 90, 100, 140, 130, 120]
                    .map((width, index) => <col key={index} style={{ width }} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => sortBy('member_code')}>รหัสสมาชิก{arrow('member_code')}</th>
                    <th className="sortable" onClick={() => sortBy('nickname')}>ชื่อเล่น{arrow('nickname')}</th>
                    <th>สาขาที่ลงทะเบียน</th>
                    <th>เบอร์โทรศัพท์</th>
                    <th>ความต้องการพิเศษล่าสุด</th>
                    <th className="num sortable" onClick={() => sortBy('total_spend')}>ยอดรวมการซื้อ{arrow('total_spend')}</th>
                    <th className="num sortable" onClick={() => sortBy('visit_count')}>จำนวนครั้งที่มา{arrow('visit_count')}</th>
                    <th className="sortable" onClick={() => sortBy('last_visit_at')}>มาครั้งล่าสุดเมื่อ{arrow('last_visit_at')}</th>
                    <th className="sortable" onClick={() => sortBy('first_name')}>ชื่อจริง{arrow('first_name')}</th>
                    <th className="sortable" onClick={() => sortBy('last_name')}>นามสกุล{arrow('last_name')}</th>
                    <th>อีเมล</th><th>วันเกิด</th><th>เพศ</th><th>ช่วงอายุ</th>
                    <th>สัญชาติ</th><th>อาชีพ</th><th className="sortable" onClick={() => sortBy('created_at')}>สร้างเมื่อ{arrow('created_at')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.customers.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        #{c.memberCode}
                        {c.tierCode && <span className="badge badge-purple" style={{ marginLeft: 6 }}><IconCrown size={16} /></span>}
                      </td>
                      <td style={{ fontWeight: 600 }}>{c.nickname || '-'}</td>
                      <td>{c.registeredBranchCode || c.registeredBranchName || '-'}</td>
                      <td>{c.phone || '-'}</td>
                      <td>{c.latestRequirement || '-'}</td>
                      <td className="num">{money(c.totalSpend)}</td>
                      <td className="num">{c.visitCount}</td>
                      <td>{c.lastVisitAt ? dateOf(c.lastVisitAt) : '-'}</td>
                      <td>{c.firstName || '-'}</td>
                      <td>{c.lastName || '-'}</td>
                      <td>{c.email || '-'}</td>
                      <td>{c.birthDate || '-'}</td>
                      <td>{GENDER_TH[c.gender] || '-'}</td>
                      <td>{c.ageBandLabel || '-'}</td>
                      <td>{c.nationalityNameTh || '-'}</td>
                      <td>{c.occupationNameTh || '-'}</td>
                      <td>{c.createdAt ? dateOf(c.createdAt) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {creating && (
        <CreateCustomer
          lookups={lookups.data}
          branchId={branchId}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); list.reload(); }}
        />
      )}

    </>
  );
}

function CreateCustomer({ lookups, branchId, onClose, onSaved }) {
  const [form, setForm] = useState({ gender: 'unspecified' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await customerApi.create({ ...form, registeredBranchId: branchId });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="สร้างลูกค้า"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>ยกเลิก</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      {error && <div className="login-error">{error}</div>}
      <div className="grid-2">
        <div className="field"><label>ชื่อเล่น</label><input onChange={set('nickname')} /></div>
        <div className="field"><label>เบอร์โทรศัพท์</label><input onChange={set('phone')} /></div>
        <div className="field"><label>ชื่อจริง</label><input onChange={set('firstName')} /></div>
        <div className="field"><label>นามสกุล</label><input onChange={set('lastName')} /></div>
        <div className="field">
          <label>เพศ</label>
          <select onChange={set('gender')} value={form.gender}>
            <option value="unspecified">ไม่ระบุ</option>
            <option value="female">หญิง</option>
            <option value="male">ชาย</option>
            <option value="other">อื่น ๆ</option>
          </select>
        </div>
        <div className="field"><label>วันเกิด</label><input type="date" onChange={set('birthDate')} /></div>
        <div className="field">
          <label>สัญชาติ</label>
          <select onChange={set('nationalityCode')}>
            <option value="">ไม่ระบุ</option>
            {(lookups?.nationalities || []).map((n) => (
              <option key={n.code} value={n.code}>{n.name_th || n.name_en}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>อาชีพ</label>
          <select onChange={set('occupationId')}>
            <option value="">ไม่ระบุ</option>
            {(lookups?.occupations || []).map((o) => (
              <option key={o.id} value={o.id}>{o.name_th || o.name_en}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="field"><label>หมายเหตุ</label><textarea rows={2} onChange={set('note')} /></div>
    </Modal>
  );
}
